import Game from "../game";
import Scene from "./scene";
import Element from "../element/element";
import Level, { LevelParams } from "../level";
import LevelScene from "./LevelScene";

import { randomBetween2Integers } from "../../utils/math";
import { CoordsType, getAngleBy2XY, getReverseAngle, getXYByDistanceAndAngle } from "../../utils/geometry";


export class LevelChoice extends Element {
  level!: Level;
  nextChoices: LevelChoice[] = [];
  previousChoices: LevelChoice[] = [];
  isCompleted = false;
  isLastChoice = false;

  get canClick(): boolean {
    return this?.previousChoices.filter(c => c.isLastChoice).length > 0 || false;
  }

  update(modifier: number) {
    return this;
  }

  draw(): CanvasRenderingContext2D {
    this.nextChoices.forEach((nextChoice) => {
      if (this.isLastChoice) {
        this.context.strokeStyle = 'yellow';
      } else if (this.isCompleted) {
        this.context.strokeStyle = 'green';
      } else {
        this.context.strokeStyle = 'red';
      }
      this.context.beginPath();
      const angleBetweenMeAndNextChoice = getAngleBy2XY({
        x1: this.x,
        y1: this.y,
        x2: nextChoice.x,
        y2: nextChoice.y
      });
      const distanceBetweenChoiceAndLine = this.width / 2 + 10;
      const startLineCoords = getXYByDistanceAndAngle({
        x: this.x,
        y: this.y,
        distance: distanceBetweenChoiceAndLine,
        angle: angleBetweenMeAndNextChoice
      });
      const endLineCoords = getXYByDistanceAndAngle({
        x: nextChoice.x,
        y: nextChoice.y,
        distance: distanceBetweenChoiceAndLine,
        angle: getReverseAngle(angleBetweenMeAndNextChoice)
      });
      this.context.moveTo(startLineCoords.x, startLineCoords.y);
      this.context.setLineDash([5, 15]);
      this.context.lineTo(endLineCoords.x, endLineCoords.y);
      this.context.stroke();
      this.context.setLineDash([]);
    }); // lines to next choices
    const isHovered = Game.instance.cursor.isHoverElem(this);

    this.context.strokeStyle = (this.isCompleted
      ? 'green'
      : (isHovered && this.canClick)
        ? 'yellow'
        : 'red'
    );

    /* label */
    const text = this.isCompleted
      ? this.isLastChoice 
        ? 'You are here'
        : 'Completed'
      : 'Jump point detected'
    this.context.fillStyle = this.context.strokeStyle;
    this.context.font = "12px serif";
    const textSize = this.context.measureText(text);
    this.context.fillText(text, this.x - textSize.width / 2, this.y - this.height / 2 - 10);

    /* Draw 2 concentric circles to indicate the level */
    this.context.beginPath();
    this.context.arc(this.x, this.y, this.width / 2, 0, 2 * Math.PI);
    this.context.stroke();
    this.context.beginPath();
    this.context.arc(this.x, this.y, this.width / 4, 0, 2 * Math.PI);
    this.context.stroke();

    // Draw the player's capital ship near the last finished level choice
    if (this.isLastChoice && Game.instance.player?.capitalShip) {
      const capitalShip = Game.instance.player.capitalShip;
      const shipX = this.x + this.width / 2 + 60; // Position to the right of the choice
      const shipY = this.y;

      if (capitalShip.sprite && capitalShip.sprite.image) {
        const sizeRatio = capitalShip.sprite.image.width / this.width
        const shipWidth = capitalShip.sprite.image.width / sizeRatio;
        const shipHeight = capitalShip.sprite.image.height / sizeRatio;

        this.context.save();
        this.context.translate(shipX, shipY);
        this.context.rotate(Math.PI / 180 * -(- 90)); // 0° lookAngle
        this.context.drawImage(
          capitalShip.sprite.image,
          -shipWidth / 2,
          - shipHeight / 2,
          shipWidth,
          shipHeight
        );
        this.context.restore();
      }
    }

    if (Game.instance.cursor.isHoverElem(this)) {
      this.drawLevelInfos();
    }

    return this.context;
  }

  drawLevelInfos() {
    const level = this.level;
    if (!level) return;

    let rectX = this.x + this.width / 2 + 10;
    let rectY = this.y - this.height / 2;
    const rectWidth = 200;
    const rectHeight = 100;

    // Adjust position to prevent overflow from the scene
    if (rectX < 0) {
      rectX = 10;
    }
    else if (rectX + rectWidth > this.scene.width) {
      rectX = this.x - this.width / 2 - rectWidth - 10;
    }
    if (rectY < 0) {
      rectY = 10;
    }
    else if (rectY + rectHeight > this.scene.height) {
      rectY = this.y - rectHeight;
    }


    this.context.fillStyle = 'rgba(20, 20, 20, 0.9)';
    this.context.fillRect(rectX, rectY, rectWidth, rectHeight);

    this.context.fillStyle = 'white';
    this.context.font = '12px Arial';
    Object.entries(this.level.description).forEach(([label, value], index) => {
      this.context.fillText(`${label}: ${value}`, rectX + 10, rectY + (20 * (index + 1)));
    })

  }

  startLevel() {
    this.level.onInit = (level) => {
      const x = level.scene.width / 2, y = level.scene.height / 2;
      level.scene.camera.goToCoords(x, y);
      level.scene.camera.zoom(0.5);
      Game.instance.controlSchemes.rts.disable();

      const destinationCoords = Game.instance.controlSchemes.rts.getCoordsForMultipleElements({ x, y }, Game.instance.player.jumpedOutShips);
      for (const index in Game.instance.player.jumpedOutShips) {
        setTimeout(() => {
          const ship = Game.instance.player.jumpedOutShips[index];
          ship.scene = Game.instance.scene as LevelScene;
          ship.x = destinationCoords[ship.id].x;
          ship.y = destinationCoords[ship.id].y;
          ship.jumpIn();

          if (Number(index) === Game.instance.player.jumpedOutShips.length - 1) {
            Game.instance.player.jumpedOutShips = []; // clear variable on last iteration
          }

        }, Number(index) * randomBetween2Integers(10, 100) + 1000); // add some randomness to the jump timing

        if (Number(index) === 0) {
          Game.instance.controlSchemes.rts.enable();
        }
      }
    };
    this.scene.music.pause();
    this.level.start();
  }
}

export default class LevelSelectorScene extends Scene {
  levelsNumber = 4;
  choicesNumber = 2;
  marginWidth = 100;
  distanceBetweenChoices!: number;
  availableChoices!: LevelChoice;

  create() {
    super.create();
    this.distanceBetweenChoices = (this.width - this.marginWidth * 2) / (this.levelsNumber - 1)
    this.availableChoices = this.generateAllChoices();
    return this;
  }

  generateChoice({ x, y }: CoordsType, levelParams = {}): LevelChoice {
    const choice = new LevelChoice({
      x,
      y,
      width: 50,
      height: 50,
      scene: this,
    });

    choice.level = new Level(levelParams); // Level with procedural attributes

    choice.onClick = () => {
      if (!choice.canClick) return;

      choice.previousChoices.forEach(c => c.isLastChoice = false);
      choice.isCompleted = true;
      choice.isLastChoice = true;
      choice.startLevel();
      Game.instance.paused = false;
    };
    return choice.create();
  }

  /**
   * Generates a tree-like structure of level choices.
   * 
   * The tree structure follows this pattern:
   * 
   *       [First Choice]
   *            |
   *     [Choice 1]                                [Choice 2]                                            [Choice 3] 
   *         |                                          |                                                    |  
   *   [Choice 1-1] [Choice 1-2] [Choice 1-3]      [Choice 2-1] [Choice 2-2] [Choice 2-3]                 [Choice 3-1] [Choice 3-2] [Choice 3-3]
   *        |          |          |                     |          |          |                             |          |          |
   *      ...        ...        ...                    ...        ...        ...                           ...        ...        ...
   *                                                                |
   *                                                          [Final Choice]
   * 
   * Each level (except the last) has 'choicesNumber' children.
   * The final level has only one choice.
   * 
   * @returns The root choice of the generated tree
   */
  generateAllChoices(): LevelChoice {
    const firstChoiceCoords: CoordsType = { x: this.marginWidth, y: this.height / 2 };

    const firstLevelParams: LevelParams = {
      size: 'Medium',
      hasWarFog: false,
    };
    const firstChoice = this.generateChoice(firstChoiceCoords, firstLevelParams);
    firstChoice.isLastChoice = true;
    firstChoice.isCompleted = true;

    const finalChoice = this.generateChoice({
      x: firstChoiceCoords.x + (this.levelsNumber - 1) * this.distanceBetweenChoices,
      y: firstChoiceCoords.y,
    });
    let choicesToProcess = [firstChoice];

    for (let currentLevelNumber = 1; currentLevelNumber < this.levelsNumber; currentLevelNumber++) {
      const newChoices: LevelChoice[] = [];

      choicesToProcess.forEach((choice) => {
        if (currentLevelNumber === this.levelsNumber - 1) {
          finalChoice.previousChoices.push(choice);
          choice.nextChoices = [finalChoice]; // last choice remaining
        } else {
          for (let currentChoiceIndex = 0; currentChoiceIndex < this.choicesNumber; currentChoiceIndex++) {
            const minY = this.marginWidth * 2; // why * 2 lol
            const maxY = this.height - this.marginWidth * 2;
            const availableHeight = (maxY - minY) / currentLevelNumber;
            const yDistanceBetweenNextChoices = availableHeight / (this.choicesNumber - 1);

            const nextChoiceY = choice.y - availableHeight / 2 + (yDistanceBetweenNextChoices * currentChoiceIndex);
            const nextChoice = this.generateChoice({
              x: choice.x + this.distanceBetweenChoices,
              y: nextChoiceY,
            });
            nextChoice.previousChoices = [choice];
            choice.nextChoices.push(nextChoice);
            newChoices.push(nextChoice);
          }
        }
      });

      choicesToProcess = newChoices;
    }

    return firstChoice;
  }
}